"use client";

import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import {
  useSearchUsersGraphQL,
  useFetchMoreUsers,
} from "@/lib/hooks/api/dashboard/dashboard-graphql";
import { useAdminUsers } from "@/lib/hooks/api/admin";
import { ResponsiveCommandMultiSelect } from "@/components/ui/responsive-select/ResponsiveCommandMultiSelect";
import { cn } from "@/lib/utils";
import { addRecent } from "@/components/ui/responsive-select/recents";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import { normalizeUserIds } from "./MultiUserSelect/normalize-user-ids";
import { buildUserOptions } from "./MultiUserSelect/user-options";

/**
 * Shared multi-user picker (CRM forms, advanced filters, assignment surfaces).
 *
 * Id normalization lives in `MultiUserSelect/normalize-user-ids.ts` and option
 * assembly in `MultiUserSelect/user-options.tsx` (module-size lane L17).
 * Behaviour is pinned by `tests/components-ui/MultiUserSelect.test.tsx`.
 */
interface MultiUserSelectProps {
  currentUserIds?: string[] | null;
  onUsersSelect: (userIds: string[]) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  showUnassigned?: boolean;
  showAllUsers?: boolean;
  maxUsers?: number;
  pageSize?: number;
  /**
   * @deprecated No longer gates width — `MultiUserSelect` always fills its row
   * now, matching every other component in the select family
   * (ClientSelectWithModal, CompanySelectWithModal, TicketSelectWithModal all
   * ignore this same prop and are unconditionally full-width). Kept for
   * backward-compatible call sites.
   */
  fullWidth?: boolean;
  className?: string;
  autoOpen?: boolean;
  /** When true, expand inline (search + list below) instead of popover. Default true for consistent UX (e.g. tables, modals, iPad). Pass false for popover. */
  expandInline?: boolean;
  /** When expandInline: "floating" = trigger stays, dropdown floats below (matches Company select); "replace" = trigger replaced by panel. Default "floating". */
  expandInlineVariant?: "replace" | "floating";
  /** Label for the top "current user" shortcut option. Defaults to the
   * assignment phrasing; filters pass a filter-appropriate label (e.g. "Me"). */
  meOptionLabel?: string;
  /** Description under the "current user" shortcut option. */
  meOptionDescription?: string;
}

export const MultiUserSelect: React.FC<MultiUserSelectProps> = ({
  currentUserIds = [],
  onUsersSelect,
  disabled = false,
  placeholder = "Assign to users...",
  showUnassigned = true,
  showAllUsers = true,
  maxUsers = 10,
  pageSize = 10,
  fullWidth: _fullWidth = false,
  className,
  autoOpen = false,
  expandInline = true,
  expandInlineVariant = "floating",
  meOptionLabel = "Assign to Me",
  meOptionDescription = "Assign to yourself",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserIdFromAuth, setCurrentUserIdFromAuth] = useState<string | null>(null);
  /** Last selection failure, shown in place with a retry. */
  const [selectionError, setSelectionError] = useState<string | null>(null);
  /** The ids of the attempt that failed, so Retry replays exactly it. */
  const lastAttemptRef = useRef<string[] | null>(null);
  /** Mirrors `currentUserIdFromAuth` for reads inside an in-flight callback. */
  const authUserIdRef = useRef<string | null>(null);

  // Normalize input to always be an array
  const normalizedUserIds = useMemo(() => normalizeUserIds(currentUserIds), [currentUserIds]);

  // Debounced search
  const { searchValue, debouncedSearchValue, setSearchValue } = useDebouncedSearch({
    initialValue: "",
    delay: 300,
  });

  /** Ask auth who is signed in. Returns null when it cannot be determined. */
  const fetchAuthUserId = useCallback(async (): Promise<string | null> => {
    try {
      const supabase = getBrowserSupabaseClient();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return user?.id ?? null;
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  }, []);

  // Warm the id up front so the common path needs no extra round trip. The
  // SELECTION path no longer depends on this having finished — see below.
  useEffect(() => {
    void (async () => {
      const id = await fetchAuthUserId();
      if (!id) return;
      authUserIdRef.current = id;
      setCurrentUserIdFromAuth(id);
    })();
  }, [fetchAuthUserId]);

  // GraphQL hooks for users with pagination
  const {
    users,
    pageInfo,
    isLoading: usersLoading,
    error,
    fetchMore,
  } = useSearchUsersGraphQL(
    debouncedSearchValue,
    pageSize,
    true // isActive
  );

  const fetchMoreUsers = useFetchMoreUsers();
  /** Full employee directory — selected IDs often aren't on the first search page */
  const { data: userDirectory } = useAdminUsers();

  const options = useMemo(
    () =>
      buildUserOptions({
        meOptionLabel,
        meOptionDescription,
        showUnassigned,
        currentUserIds,
        normalizedUserIds,
        userDirectory,
        searchResults: users,
      }),
    [
      users,
      showUnassigned,
      currentUserIds,
      userDirectory,
      normalizedUserIds,
      meOptionLabel,
      meOptionDescription,
    ]
  );

  const handleUsersSelect = useCallback(
    async (selectedIds: string[]) => {
      if (maxUsers && selectedIds.length > maxUsers) {
        return; // Don't allow more than max
      }

      lastAttemptRef.current = selectedIds;
      setIsLoading(true);
      setSelectionError(null);
      try {
        // Resolve the "me" sentinel AT SUBMIT, not from whatever the warm-up
        // effect happened to have finished. `currentUserIdFromAuth` starts null
        // and is filled asynchronously, so a pick made inside that window used
        // to forward the literal string "me" to the caller and on to the API.
        let meId = currentUserIdFromAuth ?? authUserIdRef.current;
        const needsMe = selectedIds.includes("me");
        if (needsMe && !meId) {
          meId = await fetchAuthUserId();
          if (meId) {
            authUserIdRef.current = meId;
            setCurrentUserIdFromAuth(meId);
          }
        }
        if (needsMe && !meId) {
          // Nobody is signed in, or auth is unreachable. Forwarding "me" would
          // send a non-id to the API; refuse and say so instead.
          setSelectionError("Could not confirm who you are signed in as.");
          return;
        }

        const processedIds = selectedIds.map((id) => (id === "me" && meId ? meId : id));

        await onUsersSelect(processedIds);

        // Record recent selections
        processedIds.forEach((userId) => {
          if (userId !== "unassigned" && userId !== "all-users") {
            const u = users?.find((x) => x.id === userId);
            const label = u
              ? u.first_name && u.last_name
                ? `${u.first_name} ${u.last_name}`
                : u.email
              : userId;
            try {
              addRecent("users", { id: userId, label }, 8);
            } catch {
              // Recents are a localStorage convenience: a quota/privacy-mode
              // failure must never break the selection the user just made.
            }
          }
        });
      } catch (error) {
        // Was console-only: the user saw the picker return to normal and had no
        // idea the assignment had not been saved.
        console.error("Error selecting users:", error);
        setSelectionError("Could not save that selection.");
      } finally {
        setIsLoading(false);
      }
    },
    [onUsersSelect, currentUserIdFromAuth, fetchAuthUserId, users, maxUsers]
  );

  /** Replay the exact selection that failed. */
  const retryLastSelection = useCallback(() => {
    const ids = lastAttemptRef.current;
    if (!ids) return;
    void handleUsersSelect(ids);
  }, [handleUsersSelect]);

  // Handle scroll to load more
  const handleLoadMore = useCallback(async () => {
    if (pageInfo?.hasNextPage && !usersLoading) {
      await fetchMoreUsers(fetchMore, pageInfo.endCursor, debouncedSearchValue, pageSize, true);
    }
  }, [pageInfo, usersLoading, fetchMore, fetchMoreUsers, debouncedSearchValue, pageSize]);

  const handleValueChange = useCallback(
    (selectedIds: string[]) => {
      void handleUsersSelect(selectedIds);
    },
    [handleUsersSelect]
  );

  const handleSearchChange = useCallback(
    (val: string) => {
      setSearchValue(val);
    },
    [setSearchValue]
  );

  return (
    <>
    <ResponsiveCommandMultiSelect
      value={normalizedUserIds}
      onValueChange={handleValueChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled || isLoading || usersLoading}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      isSearching={usersLoading}
      className={cn("w-full", className)}
      title="Select Users"
      searchPlaceholder="Search users..."
      enableRecents={true}
      recentKey="users"
      recentMax={8}
      autoOpen={autoOpen}
      expandInline={expandInline}
      expandInlineVariant={expandInlineVariant}
      onLoadMore={handleLoadMore}
      hasMore={!!pageInfo?.hasNextPage}
      isLoadingMore={!!users && users.length > 0 && !!usersLoading}
    />
    {selectionError && (
      /* ERROR_STATES_ACTIONABLE: the fix is offered here, not described
         elsewhere. Rendered as a sibling (not a wrapper) so the picker's own
         layout is untouched when there is nothing to report. */
      <div
        role="alert"
        className="mt-1 flex items-center gap-1.5 text-xs text-destructive"
      >
        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
        <span>{selectionError}</span>
        <button
          type="button"
          onClick={retryLastSelection}
          disabled={isLoading}
          className="inline-flex items-center gap-1 font-medium underline underline-offset-2 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          <RefreshCw className="h-3 w-3" />
          Retry
        </button>
      </div>
    )}
    </>
  );
};
